<template>
  <v-container class="will-summary">
    <div v-if="!loading">
      <div v-if="errorMessage">
        <h3 class="text-center mt-10">{{ errorMessage }}</h3>
      </div>
      <div v-else>
        <v-row>
          <v-col cols="12" sm="1">
            <router-link
              :to="{ name: referrerRoute, params: { id: referrerId } }"
              class="back-arrow"
              ><v-icon large color="mid-grey">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="8">
            <h1>
              Will Summary
              <span v-if="testator.fullName">for {{ testator.fullName }}</span
              ><span v-if="testator.deceased"> (DECEASED)</span
              ><span v-if="testator.doNotContact"> (DO NOT CONTACT)</span>
            </h1>
          </v-col>
          <v-col cols="12" md="3" class="text-right">
            <v-menu
              offset-y
              :close-on-content-click="false"
              content-class="options-menu"
            >
              <template #activator="{ on, attrs }">
                <v-btn dark v-bind="attrs" v-on="on">
                  <v-icon small>{{ icons.mdiAccountCogOutline }}</v-icon
                  >Options
                </v-btn>
              </template>
              <v-list>
                <v-list-item v-if="!testator.acceptedTerms" class="pa-0">
                  <CreateActivationLink :testator="testator" />
                </v-list-item>
                <v-list-item class="pa-0">
                  <LoginAsTestator
                    :testator="testator"
                    @loginActioned="showLoginActioned"
                  />
                </v-list-item>
                <v-list-item v-if="isAdmin" class="pa-0">
                  <EditUserDialog
                    :id="testator.userId"
                    @userUpdated="$apollo.queries.testator.refetch()"
                  />
                </v-list-item>
                <v-list-item v-if="showTransferTestators" class="pa-0">
                  <TransferTestators
                    :advisor-id="testator.userId"
                    :company-id="testator.company.id"
                    :advisor-name="name"
                  />
                </v-list-item>
                <v-list-item v-if="showAdvisorReferralLink" class="pa-0">
                  <ReferralLink :testator="testator" />
                </v-list-item>
                <v-list-item v-if="showAuditTrail" class="pa-0">
                  <v-btn
                    depressed
                    large
                    block
                    color="white"
                    class="ma-0"
                    @click="
                      $router.push('/advisors/audit-trail/' + testator.userId)
                    "
                  >
                    Audit trail
                  </v-btn>
                </v-list-item>
                <v-list-item class="pa-0" v-if="testator.doNotContact == 0">
                  <MarkAsDoNotContact
                    :testator="testator"
                    :referrer-route="referrerRoute"
                    :referrer-id="referrerId"
                  />
                </v-list-item>
                <v-list-item class="pa-0" v-else>
                  <UnmarkAsDoNotContact
                    :testator="testator"
                    :referrer-route="referrerRoute"
                    :referrer-id="referrerId"
                  />
                </v-list-item>
                <v-list-item class="pa-0" v-if="isAdmin && !testator.deceased">
                  <MarkAsDeceased
                    :testator="testator"
                    :referrer-route="referrerRoute"
                    :referrer-id="referrerId"
                  />
                </v-list-item>
                <v-list-item
                  class="pa-0"
                  v-else-if="isAdmin && testator.deceased"
                >
                  <UnmarkAsDeceased
                    :testator="testator"
                    :referrer-route="referrerRoute"
                    :referrer-id="referrerId"
                  />
                </v-list-item>
                <v-list-item class="pa-0" v-if="isAdmin && latestWillIsValid">
                  <VaultStorageSettings
                    :testator="testator"
                    :referrer-route="referrerRoute"
                    :referrer-id="referrerId"
                    :key="testator.dateStored"
                  />
                </v-list-item>
                <v-list-item v-if="canDownloadOmpPdf" class="pa-0">
                  <DownloadOmProtectPdf :testator="testator" />
                </v-list-item>
                <v-list-item class="pa-0" v-if="isAdmin">
                  <DeleteUser
                    :testator="testator"
                    :referrer-route="referrerRoute"
                    :referrer-id="referrerId"
                  />
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="8" offset-lg="2">
            <v-card
              class="mb-0"
              v-if="speakToDfaAdvisorEnabled || ratherChatEnabled"
            >
              <v-card-title> Lead Transfer </v-card-title>
              <v-card-text>
                <div class="d-flex flex-column flex-md-row justify-end w-100">
                  <GradientButton
                    v-if="speakToDfaAdvisorEnabled"
                    :title="'Speak to DFA advisor'"
                    :small="true"
                    :fill="true"
                    :disabled="!speakToDfaAdvisorValid || !ratherChatValid"
                    @gradientButtonClicked="handleSpeakToDfaAdvisor"
                    :callout="'none'"
                    class="mb-2 mb-md-0 mr-md-2"
                  />
                  <GradientButton
                    v-if="ratherChatEnabled"
                    :title="'Interested in Funeral cover'"
                    :small="true"
                    :fill="true"
                    :disabled="
                      !ratherChatValid ||
                      !speakToDfaAdvisorValid ||
                      testator.cellNumber == null ||
                      testator.cellNumber == ''
                    "
                    @gradientButtonClicked="handleInterestedInFuneralCover"
                    :callout="'none'"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="8" offset-lg="2">
            <RequestACallCard :testator="testator" />
            <TestatorCard :testator="testator" :relations="relations" />
            <PeopleCard :testator="testator" :relations="relations" />
            <AssetsCard :testator="testator" />
            <SpecificDonationsCard :testator="testator" />
            <RemainderOfEstateCard :testator="testator" />
            <LetterOfWishesCard :testator="testator" :relations="relations" />
            <OmProtectEstateCostsCard
              v-if="showOmProtectEstateCostsCard"
              :testator="testator"
            />
            <EstateCostsCard
              v-else-if="isZAWill && showEstateCostsCard"
              :testator="testator"
            />
            <InsuranceCard
              v-if="isZAWill && showInsuranceCard"
              :testator="testator"
              :relations="relations"
            />
            <WillLocationCard :testator="testator" :relations="relations" />
            <WillVerificationCard
              :testator="testator"
              @refreshTestator="refreshTestator"
            />
            <TestatorDocumentsCard :testator="testator" />
            <NotesCard :testator="testator" />
          </v-col>
        </v-row>
      </div>
    </div>

    <div v-else class="text-center">
      <v-progress-circular indeterminate color="primary" />
    </div>
    <LoginAsTestatorOTP
      :testator="testator"
      :show-dialog="showOtpDialog"
      :message="otpDialogMessage"
      @otpSubmitted="otpSubmitted"
    />
    <ErrorSnackbar v-model="showError" :error="snackBarErrorMessage" />

    <!-- DFA Advisor Comment Dialog -->
    <v-dialog v-model="showDfaDialog" max-width="440px" rounded="0">
      <v-card rounded="0">
        <v-card-title class="text-h6">
          <v-row no-gutters align="center">
            <v-col cols="11" class="d-flex align-center"> Add a comment </v-col>
            <v-col cols="1" class="d-flex align-center">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="showDfaDialog = false"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider></v-divider>
        <div class="pa-4">
          <ValidationObserver ref="dfaObserver">
            <v-form>
              <ValidationProvider
                id="dfa_comment"
                v-slot="{ errors }"
                name="dfa_comment"
                rules="required"
              >
                <v-textarea
                  v-model="dfaComment"
                  label="Enter your comment"
                  outlined
                  dense
                  height="300"
                  hide-details
                  class="comment-textarea"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-form>
          </ValidationObserver>
          <div class="d-flex justify-end mt-4">
            <GradientButton
              :title="'Submit'"
              :small="true"
              @gradientButtonClicked="submitDfaComment"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import {
    latestValidUploadedWill,
    latestWillVersion,
  } from '../../../constants/js/will-verification';
  import ErrorSnackbar from '../../common/ErrorSnackbar.vue';
  import {
    TESTATOR_QUERY,
    SPEAK_TO_DFA_ADVISOR_MUTATION,
    INTERESTED_IN_FUNERAL_COVER_MUTATION,
  } from '../../../constants/graphql/testators';
  import { SEND_OTP_MUTATION } from '../../../constants/graphql/users';
  import { VERIFY_OTP_MUTATION } from '../../../constants/graphql/users';
  import { getRelations } from '../../../constants/js/people';
  import WillVerificationCard from './WillVerificationCard.vue';
  import TestatorDocumentsCard from './TestatorDocumentsCard.vue';
  import InsuranceCard from './InsuranceCard.vue';
  import TestatorCard from './TestatorCard.vue';
  import PeopleCard from './PeopleCard.vue';
  import SpecificDonationsCard from './SpecificDonationsCard.vue';
  import LetterOfWishesCard from './LetterOfWishesCard.vue';
  import RemainderOfEstateCard from './RemainderOfEstateCard.vue';
  import AssetsCard from './AssetsCard.vue';
  import CreateActivationLink from './CreateActivationLink.vue';
  import DeleteUser from './DeleteUser.vue';
  import EstateCostsCard from './EstateCostsCard.vue';
  import OmProtectEstateCostsCard from './OmProtectEstateCostsCard.vue';
  import EditUserDialog from './EditUserDialog.vue';
  import LoginAsTestator from '../LoginAsTestator.vue';
  import LoginAsTestatorOTP from '../LoginAsTestatorOTP.vue';
  import WillLocationCard from './WillLocationCard.vue';
  import TransferTestators from './TransferTestators.vue';
  import ReferralLink from '../ReferralLink.vue';
  import NotesCard from './notes/NotesCard.vue';
  import RequestACallCard from './RequestACallCard.vue';
  import MarkAsDeceased from './MarkAsDeceased.vue';
  import MarkAsDoNotContact from './MarkAsDoNotContact.vue';
  import UnmarkAsDeceased from './UnmarkAsDeceased.vue';
  import UnmarkAsDoNotContact from './UnmarkAsDoNotContact.vue';
  import VaultStorageSettings from './VaultStorageSettings.vue';
  import DownloadOmProtectPdf from './DownloadOmProtectPdf.vue';
  import { ValidationObserver, ValidationProvider } from 'vee-validate';
  import { mdiClose } from '@mdi/js';

  export default {
    name: 'WillSummary',
    components: {
      ErrorSnackbar,
      WillVerificationCard,
      TestatorDocumentsCard,
      InsuranceCard,
      TestatorCard,
      PeopleCard,
      SpecificDonationsCard,
      LetterOfWishesCard,
      RemainderOfEstateCard,
      AssetsCard,
      CreateActivationLink,
      DeleteUser,
      MarkAsDeceased,
      MarkAsDoNotContact,
      UnmarkAsDeceased,
      UnmarkAsDoNotContact,
      VaultStorageSettings,
      DownloadOmProtectPdf,
      EstateCostsCard,
      OmProtectEstateCostsCard,
      EditUserDialog,
      LoginAsTestator,
      LoginAsTestatorOTP,
      WillLocationCard,
      TransferTestators,
      ReferralLink,
      NotesCard,
      RequestACallCard,
      ValidationObserver,
      ValidationProvider,
    },
    props: {
      referrerRoute: {
        type: String,
        default: 'AdvisorDashboard',
      },
      referrerId: {
        type: String,
        default: null,
      },
    },
    data() {
      return {
        loading: true,
        testator: {},
        relations: {},
        errorMessage: null,
        snackBarErrorMessage: null,
        showOtpDialog: false,
        otpDialogMessage: '',
        showError: false,
        showDfaDialog: false,
        dfaComment: '',
        icons: {
          mdiClose: mdiClose,
        },
      };
    },
    methods: {
      refreshTestator() {
        this.$apollo.queries.testator.refetch();
      },
      showLoginActioned(testator) {
        this.sendOtpToTestator(testator);
      },
      verifyOtp(testator, otp) {
        const testatorId = testator.id;

        this.$apollo
          .mutate({
            mutation: VERIFY_OTP_MUTATION,
            variables: {
              testatorId,
              otp,
            },
          })
          .then(response => {
            const testatorId = this.testator.id;
            const userId = this.testator.userId;
            const willCountry = this.testator.willCountry;

            this.$store.dispatch('loginAs', {
              testatorId,
              userId,
              willCountry,
            });
            this.$router.push('/overview');
          })
          .catch(error => {
            console.log(`An error occured: ${error.message}`);
            this.showError = true;
            this.snackBarErrorMessage = error.message.replace(
              'GraphQL error: ',
              ''
            );
          });
      },
      otpSubmitted(obj) {
        this.showOtpDialog = false;
        this.verifyOtp(obj.testator, obj.otp);
      },
      sendOtpToTestator(testator) {
        const testatorId = testator.id;

        this.$apollo
          .mutate({
            mutation: SEND_OTP_MUTATION,
            variables: {
              testatorId,
            },
          })
          .then(response => {
            this.otpDialogMessage = response.data.sendOtp.message;
            if (response.data.sendOtp.status === 'proceed_with_login') {
              const testatorId = this.testator.id;
              const userId = this.testator.userId;
              const willCountry = this.testator.willCountry;

              this.$store.dispatch('loginAs', {
                testatorId,
                userId,
                willCountry,
              });
              this.$router.push('/overview');
            } else {
              this.showOtpDialog = true;
            }
          })
          .catch(error => {
            console.log(`An error occured: ${error.message}`);
          });
      },
      handleSpeakToDfaAdvisor() {
        this.showDfaDialog = true;
      },
      async submitDfaComment() {
        const isValid = await this.$refs.dfaObserver.validate();
        if (!isValid) {
          return;
        }

        this.showDfaDialog = false;
        this.$apollo
          .mutate({
            mutation: SPEAK_TO_DFA_ADVISOR_MUTATION,
            variables: {
              testatorId: this.testator.id,
              comment: this.dfaComment,
            },
            update: (cache, { data: { speakToDfaAdvisor } }) => {
              this.updateCacheAfterUpdateDfaAdvisor(cache, speakToDfaAdvisor);
            },
          })
          .then(response => {
            // Handle success if needed
          })
          .catch(error => {
            console.error('Error calling DFA advisor mutation:', error);
          });
        this.dfaComment = ''; // Reset the comment
      },
      handleInterestedInFuneralCover() {
        this.$apollo
          .mutate({
            mutation: INTERESTED_IN_FUNERAL_COVER_MUTATION,
            variables: {
              testatorId: this.testator.id,
            },
            update: (cache, { data: { interestedInFuneralCover } }) => {
              this.updateCacheAfterRatherChat(cache, interestedInFuneralCover);
            },
          })
          .then(response => {
            // Handle success if needed
          })
          .catch(error => {
            console.error('Error calling funeral cover mutation:', error);
          });
      },
      updateCacheAfterUpdateDfaAdvisor(cache, response) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        data.testator.ratherChatRequested = response.ratherChatRequested;
        data.testator.ratherChatRequestedAt = response.ratherChatRequestedAt;
        data.testator.ratherChatRequestedById =
          response.ratherChatRequestedById;
        data.testator.speakToDfaAdvisorRequested =
          response.speakToDfaAdvisorRequested;
        data.testator.speakToDfaAdvisorRequestedAt =
          response.speakToDfaAdvisorRequestedAt;
        data.testator.speakToDfaAdvisorRequestedById =
          response.speakToDfaAdvisorRequestedById;

        cache.writeQuery({ ...query, data: data });
      },
      updateCacheAfterRatherChat(cache, response) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        data.testator.ratherChatRequested = response.ratherChatRequested;
        data.testator.ratherChatRequestedAt = response.ratherChatRequestedAt;
        data.testator.ratherChatRequestedById =
          response.ratherChatRequestedById;
        data.testator.speakToDfaAdvisorRequested =
          response.speakToDfaAdvisorRequested;
        data.testator.speakToDfaAdvisorRequestedAt =
          response.speakToDfaAdvisorRequestedAt;
        data.testator.speakToDfaAdvisorRequestedById =
          response.speakToDfaAdvisorRequestedById;

        cache.writeQuery({ ...query, data: data });
      },
    },
    computed: {
      ratherChatEnabled() {
        if (this.isAdmin || this.isCallCenterAgent) {
          return this.testator.ratherChatEnabled;
        }

        return false;
      },
      speakToDfaAdvisorEnabled() {
        if (this.isAdmin || this.isCallCenterAgent) {
          return this.testator.speakToDfaAdvisorEnabled;
        }
        return false;
      },
      ratherChatValid() {
        if (!this.testator.ratherChatRequestedAt) {
          return true;
        }

        const lastRequest = new Date(this.testator.ratherChatRequestedAt);
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

        return lastRequest < twoDaysAgo;
      },
      speakToDfaAdvisorValid() {
        if (!this.testator.speakToDfaAdvisorRequestedAt) {
          return true;
        }

        const lastRequest = new Date(
          this.testator.speakToDfaAdvisorRequestedAt
        );
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

        return lastRequest < twoDaysAgo;
      },
      latestValidUpload() {
        return latestValidUploadedWill(this.testator.willVersions);
      },
      latestWillIsValid() {
        const latest = latestWillVersion(this.testator.willVersions || []);
        return latest?.status === 'VALID';
      },
      showInsuranceCard() {
        return (
          this.testator.enabledFeatures.includes('full_insurance') ||
          this.testator.enabledFeatures.includes('advisor_full_insurance')
        );
      },
      showEstateCostsCard() {
        return (
          this.isOmpCompany ||
          this.showInsuranceCard ||
          this.testator.enabledFeatures.includes('liquidity_calculator') ||
          this.testator.enabledFeatures.includes('advisor_full_insurance')
        );
      },
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      isCallCenterAgent() {
        return this.$store.getters.isCallCenterAgent;
      },
      isCompanyAdmin() {
        return this.$store.getters.isCompanyAdmin;
      },
      isAdvisor() {
        return this.$store.getters.isAdvisor;
      },

      showTransferTestators() {
        return (
          (this.isAdmin || this.isCompanyAdmin) &&
          (this.testator.user.role == 'ADVISOR' ||
            this.testator.user.role == 'COMPANY_ADMIN')
        );
      },
      showAdvisorReferralLink() {
        return (
          (this.isAdmin || this.isCompanyAdmin) &&
          (this.testator.user.role == 'ADVISOR' ||
            this.testator.user.role == 'COMPANY_ADMIN')
        );
      },
      name() {
        return this.testator.fullName
          ? this.testator.fullName
          : this.testator.email;
      },
      isZAWill() {
        return this.testator.willCountry == 'ZA';
      },
      showAuditTrail() {
        return (
          (this.isAdmin || this.isCompanyAdmin) &&
          (this.testator.user.role == 'ADVISOR' ||
            this.testator.user.role == 'COMPANY_ADMIN' ||
            this.testator.user.role == 'ADMIN' ||
            this.testator.user.role == 'FIDUCIARY_CONSULTANT')
        );
      },
      isOmpCompany() {
        return (
          this.testator?.company?.insuranceStep === 'OMP_FINANCIAL_DATA_SQ'
        );
      },
      canDownloadOmpPdf() {
        const isStaff = this.isAdmin || this.isCompanyAdmin || this.isAdvisor;
        return isStaff && this.isOmpCompany;
      },
      showOmProtectEstateCostsCard() {
        const store = this.testator?.omProtectStore;
        return (
          this.isZAWill && this.isOmpCompany && (store?.totalAssets || 0) > 0
        );
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.$route.params.id,
          };
        },
        fetchPolicy: 'network-only',
        result(response) {
          if (response.data.testator) {
            this.relations = getRelations(response.data.testator.people);
          } else {
            this.errorMessage =
              'You do not have permission to view this testator';
          }
          this.loading = false;
        },
      },
    },
  };
</script>

<style lang="scss">
  .will-summary {
    .v-card {
      margin-bottom: 30px;

      .v-card__text {
        color: rgba(0, 0, 0, 0.87);
        margin-top: 10px;
        margin-left: 10px;

        ul {
          li {
            margin-bottom: 4px;
          }
        }
      }
    }

    .letter-of-wishes {
      white-space: pre-wrap;
    }

    .comment-textarea {
      ::v-deep .v-input__control {
        min-height: 400px;

        textarea {
          min-height: 400px !important;
        }
      }
    }
  }
</style>
